// src/components/resources/tools/generators/shared/PostExportPrompt.tsx

'use client'

import Link from 'next/link'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import RiCheckboxCircleFill from '~/icons/remix/generated/RiCheckboxCircleFill'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Button } from '~/components/ui/primitives/button'
import { siteConfig } from '~/lib/config/site'
import { getGeneratorGrowthCopy } from '~/lib/resources/generator-growth-config'
import type { GeneratorVariantLink } from '~/lib/resources/generator-growth-config'
import { trackGeneratorBridgeClick } from './trackGeneratorBridge'
import { GENERATOR_GROWTH_CTA_CLASS } from './generatorGrowthStyles'

export type { GeneratorVariantLink as PostExportVariantLink }

/**
 * Props for the post-export growth prompt.
 *
 * @interface PostExportPromptProps
 * @property {boolean} open - Whether the dialog is open.
 * @property {(open: boolean) => void} onOpenChange - Radix open-change handler.
 * @property {string} resourceSlug - Generator slug for analytics.
 * @property {number} [engagementMs] - Engagement time on the tool (static snapshot).
 * @property {() => number} [getEngagementMs] - Engagement timer read at click time.
 * @property {GeneratorVariantLink[]} [variantLinks] - Optional variant deep-links.
 */
export interface PostExportPromptProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceSlug: string
  engagementMs?: number
  getEngagementMs?: () => number
  variantLinks?: readonly GeneratorVariantLink[]
}

/**
 * Shown immediately after a successful mockup export on professional-intent
 * generators. Offers SaaS intake and optional platform-variant deep links.
 *
 * @param {PostExportPromptProps} props - Component props.
 * @returns {JSX.Element} Modal prompt.
 */
export function PostExportPrompt({
  open,
  onOpenChange,
  resourceSlug,
  engagementMs,
  getEngagementMs,
  variantLinks = [],
}: PostExportPromptProps): JSX.Element {
  const copy = getGeneratorGrowthCopy(resourceSlug)
  const requestAccessPath = siteConfig.baseLinks.requestAccess

  const resolveEngagementMs = (): number | undefined => getEngagementMs?.() ?? engagementMs

  const trackClick = (targetType: 'request_access' | 'variant_page', destinationPath: string): void => {
    trackGeneratorBridgeClick({
      resourceSlug,
      placement: 'post_export',
      targetType,
      destinationPath,
      engagementMs: resolveEngagementMs(),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="border-b border-indigo-500/30 bg-indigo-600 px-6 py-5 text-white dark:bg-indigo-700">
          <div className="flex items-start gap-3">
            <RiCheckboxCircleFill className="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-300" aria-hidden />
            <div>
              <DialogHeader className="space-y-1 text-left">
                <DialogTitle className="text-lg font-semibold text-white">Mockup exported</DialogTitle>
                <DialogDescription className="text-sm text-indigo-100">
                  Your mockup is downloading. {copy.title}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <div className="space-y-5 px-6 py-5">
          <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{copy.body}</p>

          <ul className="space-y-2">
            {copy.bullets.slice(0, 2).map(bullet => (
              <li key={bullet} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-500" aria-hidden />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>

          {variantLinks.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Or try another chrome
              </p>
              <ul className="space-y-2">
                {variantLinks.map(link => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      onClick={() => trackClick('variant_page', link.href)}
                      className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-3 transition hover:border-indigo-300 hover:bg-indigo-50/80 dark:border-slate-700 dark:bg-slate-900/50 dark:hover:border-indigo-500/40 dark:hover:bg-indigo-500/10"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-900 dark:text-white">{link.label}</span>
                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{link.description}</span>
                      </span>
                      <RiArrowRightLine
                        className="h-4 w-4 flex-shrink-0 text-indigo-500 transition-transform group-hover:translate-x-0.5"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/60 sm:flex-col sm:space-x-0">
          <Link
            href={requestAccessPath}
            onClick={() => trackClick('request_access', requestAccessPath)}
            className={GENERATOR_GROWTH_CTA_CLASS}
          >
            {copy.ctaLabel}
            <RiArrowRightLine className="h-4 w-4" aria-hidden />
          </Link>
          <Button type="button" variant="ghost" className="w-full text-slate-600 dark:text-slate-400" onClick={() => onOpenChange(false)}>
            Continue editing mockup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
